// Запросы раздела «Найм»: ключи кэша, хуки чтения и изменения.
//
// Правило 11 (поведение во времени задано числами) реализуется здесь, а не в каждом экране:
//   * placeholderData: keepPreviousData — при смене отбора строки остаются на месте, а не
//     сменяются пустотой на время запроса;
//   * скелет рисуется только там, где данных нет ДАЖЕ в кэше прошлой сессии — это решает
//     ScreenState по data === undefined;
//   * спиннера поверх уже показанного числа нет нигде.
//
// Общий корень ключей 'hiring' даёт одну команду на сброс раздела. Пользуемся им экономно:
// смена этапа кандидата и правда меняет и список, и воронку, и карточку, а вот подтверждение
// одного предложенного ответа не имеет отношения ни к чёрному списку, ни к вакансиям —
// сбрасывать там весь раздел значит заново дёргать сервер шестью запросами ради одной строки.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import type { ListResponse } from '@/shared/api/types';
import type { ApiError } from '@/shared/errors/api-error';

import type {
  BlacklistCheckLogOut,
  BlacklistCheckOut,
  BlacklistDraftOut,
  BlacklistEntryIn,
  BlacklistEntryOut,
  BlacklistQuery,
  CheckStage,
  DraftDecisionIn,
  MessengerGroup,
  MessengerImportOut,
} from './api/blacklist';
import {
  checkBlacklist,
  createBlacklistEntry,
  createImport,
  decideDraft,
  eraseBlacklistEntry,
  getBlacklistEntry,
  liftBlacklistEntry,
  listBlacklist,
  listChecks,
  listDrafts,
  listImports,
  parseImport,
} from './api/blacklist';
import type {
  CandidateBrief,
  CandidateIn,
  CandidateOut,
  CandidatePatch,
  CandidateStatusChangeIn,
  CandidateStatusHistoryOut,
  CandidatesQuery,
  DuplicateReviewOut,
  DuplicateSearchQuery,
  CandidateDuplicateReport,
  FunnelOut,
  MergeOut,
  NoteOut,
  PromoteIn,
  PromoteOut,
  RecruitersOut,
  TicketCompensationOut,
} from './api/candidates';
import {
  addNote,
  addTags,
  changeCandidateStatus,
  createCandidate,
  decideDuplicate,
  fetchFunnel,
  fetchRecruiterLoad,
  fetchTicketCompensation,
  getCandidate,
  listCandidates,
  listDuplicateReviews,
  listStatusHistory,
  mergeCandidates,
  patchCandidate,
  promoteCandidate,
  searchDuplicates,
} from './api/candidates';
import type {
  AccountOut,
  BulkEditIn,
  BulkEditOut,
  BulkUploadOut,
  ExchangeOut,
  KeyKind,
  PollResultOut,
  TokenOut,
  VacancyIn,
  VacancyOut,
} from './api/platform';
import {
  buildBulkUpload,
  bulkEditVacancies,
  createAccount,
  createVacancy,
  fetchExchangeLog,
  listAccounts,
  listVacancies,
  pollApplications,
  publishVacancy,
  refreshToken,
  refreshVacancyStatus,
  setCredentials,
} from './api/platform';
import type {
  ActionResult,
  AssistantSettings,
  AssistantSettingsIn,
  DialogLog,
  DialogRow,
  LibraryDraftIn,
  LibraryEntry,
  ScriptStep,
} from './api/dialogs';
import {
  approveLibraryEntry,
  approveMessage,
  createLibraryDraft,
  deleteLibraryEntry,
  editLibraryEntry,
  discardMessage,
  fetchDialog,
  listAssistantSettings,
  listDialogs,
  listLibrary,
  listScript,
  resumeAssistant,
  retireLibraryEntry,
  saveAssistantSettings,
  sendOperatorMessage,
} from './api/dialogs';

export { newIdempotencyKey } from '@/shared/api/idempotency';

export const hiringKeys = {
  all: ['hiring'] as const,
  candidates: (params: CandidatesQuery) => ['hiring', 'candidates', params] as const,
  candidate: (id: string) => ['hiring', 'candidate', id] as const,
  statusHistory: (id: string) => ['hiring', 'status-history', id] as const,
  ticket: (id: string) => ['hiring', 'ticket', id] as const,
  duplicates: (status: string | undefined) => ['hiring', 'duplicates', status] as const,
  duplicateSearch: (params: DuplicateSearchQuery) =>
    ['hiring', 'duplicate-search', params] as const,
  funnel: (since: string | undefined, stuckDays: number) =>
    ['hiring', 'funnel', since, stuckDays] as const,
  recruiters: (since: string | undefined) => ['hiring', 'recruiters', since] as const,
  blacklist: (params: BlacklistQuery) => ['hiring', 'blacklist', params] as const,
  entry: (id: string) => ['hiring', 'entry', id] as const,
  checks: (candidateId: string | undefined) => ['hiring', 'checks', candidateId] as const,
  imports: () => ['hiring', 'imports'] as const,
  drafts: (params: { import_id?: string; verdict?: string }) =>
    ['hiring', 'drafts', params] as const,
  accounts: () => ['hiring', 'accounts'] as const,
  exchange: (accountId: string) => ['hiring', 'exchange', accountId] as const,
  vacancies: (params: { account_id?: string; state?: string }) =>
    ['hiring', 'vacancies', params] as const,
  dialogs: (params: { status?: string; account_id?: string }) =>
    ['hiring', 'dialogs', params] as const,
  dialog: (id: string) => ['hiring', 'dialog', id] as const,
  assistantSettings: () => ['hiring', 'assistant-settings'] as const,
  script: (accountId: string | undefined) => ['hiring', 'script', accountId] as const,
  library: (params: { step_code?: string; status?: string; account_id?: string }) =>
    ['hiring', 'library', params] as const,
  /**
   * Предупреждение о дубле от заведения карточки. Приезжает ВМЕСТЕ с созданной карточкой и в
   * GET карточки его уже нет: сервер отдаёт его один раз. Кладём под своим ключом, чтобы
   * карточка, открывшаяся сразу после мастера, показала то же предупреждение.
   */
  duplicateWarning: (id: string) => ['hiring', 'duplicate-warning', id] as const,
};

// ── Кандидаты ─────────────────────────────────────────────────────────────────────────────

export function useCandidatesQuery(
  params: CandidatesQuery,
): UseQueryResult<ListResponse<CandidateBrief>, ApiError> {
  return useQuery<ListResponse<CandidateBrief>, ApiError>({
    queryKey: hiringKeys.candidates(params),
    queryFn: ({ signal }) => listCandidates(params, signal),
    placeholderData: keepPreviousData,
  });
}

export function useCandidateQuery(id: string): UseQueryResult<CandidateOut, ApiError> {
  return useQuery<CandidateOut, ApiError>({
    queryKey: hiringKeys.candidate(id),
    queryFn: ({ signal }) => getCandidate(id, signal),
  });
}

export function useStatusHistoryQuery(
  id: string,
): UseQueryResult<ListResponse<CandidateStatusHistoryOut>, ApiError> {
  return useQuery<ListResponse<CandidateStatusHistoryOut>, ApiError>({
    queryKey: hiringKeys.statusHistory(id),
    queryFn: ({ signal }) => listStatusHistory(id, signal),
  });
}

/**
 * Факт компенсации билета. Его может не быть вовсе — это обычное дело, а не сбой: повторять
 * запрос за отсутствующим фактом незачем.
 */
export function useTicketCompensationQuery(
  id: string,
  enabled: boolean,
): UseQueryResult<TicketCompensationOut, ApiError> {
  return useQuery<TicketCompensationOut, ApiError>({
    queryKey: hiringKeys.ticket(id),
    queryFn: ({ signal }) => fetchTicketCompensation(id, signal),
    enabled,
    retry: false,
  });
}

/** Предупреждение о дубле, оставшееся от заведения карточки. */
export function useDuplicateWarning(id: string): CandidateDuplicateReport | undefined {
  const client = useQueryClient();
  return client.getQueryData<CandidateDuplicateReport>(hiringKeys.duplicateWarning(id));
}

export function useCreateCandidateMutation(
  idempotencyKey: string,
): UseMutationResult<CandidateOut, ApiError, CandidateIn> {
  const client = useQueryClient();
  return useMutation<CandidateOut, ApiError, CandidateIn>({
    mutationFn: (body) => createCandidate(body, idempotencyKey),
    onSuccess: (candidate) => {
      if (candidate.duplicate_warning != null && candidate.duplicate_warning.found) {
        client.setQueryData(hiringKeys.duplicateWarning(candidate.id), candidate.duplicate_warning);
      }
      void client.invalidateQueries({ queryKey: hiringKeys.all });
    },
  });
}

export function usePatchCandidateMutation(
  id: string,
): UseMutationResult<CandidateOut, ApiError, CandidatePatch> {
  const client = useQueryClient();
  return useMutation<CandidateOut, ApiError, CandidatePatch>({
    mutationFn: (body) => patchCandidate(id, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.all });
    },
  });
}

export function useCandidateStatusMutation(
  id: string,
): UseMutationResult<CandidateOut, ApiError, CandidateStatusChangeIn> {
  const client = useQueryClient();
  return useMutation<CandidateOut, ApiError, CandidateStatusChangeIn>({
    mutationFn: (body) => changeCandidateStatus(id, body),
    onSuccess: () => {
      // Этап меняет строку списка, историю статусов и всю воронку разом.
      void client.invalidateQueries({ queryKey: hiringKeys.all });
    },
  });
}

export function useAddNoteMutation(id: string): UseMutationResult<NoteOut, ApiError, string> {
  const client = useQueryClient();
  return useMutation<NoteOut, ApiError, string>({
    mutationFn: (body) => addNote(id, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.candidate(id) });
    },
  });
}

export function useAddTagsMutation(
  id: string,
): UseMutationResult<CandidateOut, ApiError, string[]> {
  const client = useQueryClient();
  return useMutation<CandidateOut, ApiError, string[]>({
    mutationFn: (tags) => addTags(id, tags),
    onSuccess: () => {
      // Метка — это ещё и отбор в списке: сбрасываем и карточку, и список.
      void client.invalidateQueries({ queryKey: hiringKeys.all });
    },
  });
}

/**
 * Перевод в сотрудники. Сбрасывает и раздел найма, и реестр людей: сотрудник появляется
 * ТАМ, и человек, нажавший «Оформить», пойдёт искать его именно в «Людях».
 */
export function usePromoteMutation(
  id: string,
  idempotencyKey: string,
): UseMutationResult<PromoteOut, ApiError, PromoteIn> {
  const client = useQueryClient();
  return useMutation<PromoteOut, ApiError, PromoteIn>({
    mutationFn: (body) => promoteCandidate(id, body, idempotencyKey),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.all });
      void client.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

// ── Дубли ─────────────────────────────────────────────────────────────────────────────────

export function useDuplicateReviewsQuery(
  status: string | undefined,
): UseQueryResult<ListResponse<DuplicateReviewOut>, ApiError> {
  return useQuery<ListResponse<DuplicateReviewOut>, ApiError>({
    queryKey: hiringKeys.duplicates(status),
    queryFn: ({ signal }) => listDuplicateReviews(status, signal),
    placeholderData: keepPreviousData,
  });
}

/**
 * Живая проверка дубля в мастере. Запускается по расфокусу поля, а не на каждой букве:
 * телефон набирается одиннадцатью нажатиями, и одиннадцать запросов подряд дали бы
 * одиннадцать мигающих предупреждений, из которых первые десять — про недонабранный номер.
 */
export function useDuplicateSearchQuery(
  params: DuplicateSearchQuery,
  enabled: boolean,
): UseQueryResult<CandidateDuplicateReport, ApiError> {
  return useQuery<CandidateDuplicateReport, ApiError>({
    queryKey: hiringKeys.duplicateSearch(params),
    queryFn: ({ signal }) => searchDuplicates(params, signal),
    enabled,
  });
}

export function useMergeMutation(): UseMutationResult<
  MergeOut,
  ApiError,
  { keepId: string; mergeId: string }
> {
  const client = useQueryClient();
  return useMutation<MergeOut, ApiError, { keepId: string; mergeId: string }>({
    mutationFn: ({ keepId, mergeId }) => mergeCandidates(keepId, mergeId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.all });
    },
  });
}

export function useDecideDuplicateMutation(): UseMutationResult<
  DuplicateReviewOut,
  ApiError,
  { reviewId: string; decision: 'merge' | 'separate'; keepId?: string }
> {
  const client = useQueryClient();
  return useMutation<
    DuplicateReviewOut,
    ApiError,
    { reviewId: string; decision: 'merge' | 'separate'; keepId?: string }
  >({
    mutationFn: ({ reviewId, decision, keepId }) => decideDuplicate(reviewId, decision, keepId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.all });
    },
  });
}

// ── Аналитика воронки ─────────────────────────────────────────────────────────────────────

export function useFunnelQuery(
  since: string | undefined,
  stuckDays: number,
): UseQueryResult<FunnelOut, ApiError> {
  return useQuery<FunnelOut, ApiError>({
    queryKey: hiringKeys.funnel(since, stuckDays),
    queryFn: ({ signal }) => fetchFunnel({ since, stuck_days: stuckDays }, signal),
    placeholderData: keepPreviousData,
  });
}

export function useRecruiterLoadQuery(
  since: string | undefined,
): UseQueryResult<RecruitersOut, ApiError> {
  return useQuery<RecruitersOut, ApiError>({
    queryKey: hiringKeys.recruiters(since),
    queryFn: ({ signal }) => fetchRecruiterLoad(since, signal),
    placeholderData: keepPreviousData,
  });
}

// ── Чёрный список ─────────────────────────────────────────────────────────────────────────

export function useBlacklistQuery(
  params: BlacklistQuery,
): UseQueryResult<ListResponse<BlacklistEntryOut>, ApiError> {
  return useQuery<ListResponse<BlacklistEntryOut>, ApiError>({
    queryKey: hiringKeys.blacklist(params),
    queryFn: ({ signal }) => listBlacklist(params, signal),
    placeholderData: keepPreviousData,
  });
}

export function useBlacklistEntryQuery(id: string): UseQueryResult<BlacklistEntryOut, ApiError> {
  return useQuery<BlacklistEntryOut, ApiError>({
    queryKey: hiringKeys.entry(id),
    queryFn: ({ signal }) => getBlacklistEntry(id, signal),
  });
}

export function useChecksQuery(
  candidateId: string | undefined,
): UseQueryResult<ListResponse<BlacklistCheckLogOut>, ApiError> {
  return useQuery<ListResponse<BlacklistCheckLogOut>, ApiError>({
    queryKey: hiringKeys.checks(candidateId),
    queryFn: ({ signal }) => listChecks({ candidate_id: candidateId, limit: 200 }, signal),
    placeholderData: keepPreviousData,
  });
}

/**
 * Сверка человека по списку прямо сейчас.
 *
 * Мутация, а не запрос, хотя она ничего не меняет в кандидате: каждая сверка ПИШЕТ строку в
 * журнал сверок, то есть у неё есть след. Запрос с автоматическим повтором наплодил бы в
 * журнале записей, которых человек не делал, и вопрос «кто и когда его сверял» перестал бы
 * иметь ответ.
 */
export function useCheckCandidateMutation(
  candidateId: string,
): UseMutationResult<BlacklistCheckOut, ApiError, CheckStage> {
  const client = useQueryClient();
  return useMutation<BlacklistCheckOut, ApiError, CheckStage>({
    mutationFn: (stage) => checkBlacklist({ candidate_id: candidateId, stage }),
    onSuccess: () => {
      // Итог сверки лежит в самой карточке (blacklist_result, blacklist_checked_at) и
      // в журнале сверок: устарели оба.
      void client.invalidateQueries({ queryKey: hiringKeys.candidate(candidateId) });
      void client.invalidateQueries({ queryKey: ['hiring', 'checks'] });
      void client.invalidateQueries({ queryKey: ['hiring', 'candidates'] });
    },
  });
}

export function useCreateEntryMutation(
  idempotencyKey: string,
): UseMutationResult<BlacklistEntryOut, ApiError, BlacklistEntryIn> {
  const client = useQueryClient();
  return useMutation<BlacklistEntryOut, ApiError, BlacklistEntryIn>({
    mutationFn: (body) => createBlacklistEntry(body, idempotencyKey),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.all });
    },
  });
}

/** Снятие записи. Не удаление: остаются дата, автор и причина. */
export function useLiftEntryMutation(
  id: string,
): UseMutationResult<BlacklistEntryOut, ApiError, string> {
  const client = useQueryClient();
  return useMutation<BlacklistEntryOut, ApiError, string>({
    mutationFn: (reason) => liftBlacklistEntry(id, reason),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.all });
    },
  });
}

/** Уничтожение записи. Настоящее удаление: карточки после него не будет. */
export function useEraseEntryMutation(
  id: string,
): UseMutationResult<Record<string, unknown>, ApiError, string> {
  const client = useQueryClient();
  return useMutation<Record<string, unknown>, ApiError, string>({
    mutationFn: (reason) => eraseBlacklistEntry(id, reason),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.all });
    },
  });
}

// ── Выгрузки мессенджера и предложения ────────────────────────────────────────────────────

export function useImportsQuery(): UseQueryResult<ListResponse<MessengerImportOut>, ApiError> {
  return useQuery<ListResponse<MessengerImportOut>, ApiError>({
    queryKey: hiringKeys.imports(),
    queryFn: ({ signal }) => listImports(signal),
    placeholderData: keepPreviousData,
  });
}

export function useDraftsQuery(params: {
  import_id?: string;
  verdict?: string;
}): UseQueryResult<ListResponse<BlacklistDraftOut>, ApiError> {
  return useQuery<ListResponse<BlacklistDraftOut>, ApiError>({
    queryKey: hiringKeys.drafts(params),
    queryFn: ({ signal }) => listDrafts({ ...params, limit: 500 }, signal),
    placeholderData: keepPreviousData,
  });
}

export function useCreateImportMutation(): UseMutationResult<
  MessengerImportOut,
  ApiError,
  { groupKind: MessengerGroup; fileId: string | null }
> {
  const client = useQueryClient();
  return useMutation<
    MessengerImportOut,
    ApiError,
    { groupKind: MessengerGroup; fileId: string | null }
  >({
    mutationFn: ({ groupKind, fileId }) => createImport(groupKind, fileId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.imports() });
    },
  });
}

export function useParseImportMutation(): UseMutationResult<
  MessengerImportOut,
  ApiError,
  { importId: string; text: string | null }
> {
  const client = useQueryClient();
  return useMutation<MessengerImportOut, ApiError, { importId: string; text: string | null }>({
    mutationFn: ({ importId, text }) => parseImport(importId, text),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.imports() });
      void client.invalidateQueries({ queryKey: ['hiring', 'drafts'] });
    },
  });
}

export function useDecideDraftMutation(): UseMutationResult<
  BlacklistDraftOut,
  ApiError,
  { draftId: string; body: DraftDecisionIn }
> {
  const client = useQueryClient();
  return useMutation<BlacklistDraftOut, ApiError, { draftId: string; body: DraftDecisionIn }>({
    mutationFn: ({ draftId, body }) => decideDraft(draftId, body),
    onSuccess: () => {
      // Решение «в чёрный список» заводит запись: список записей устарел вместе с очередью.
      void client.invalidateQueries({ queryKey: ['hiring', 'drafts'] });
      void client.invalidateQueries({ queryKey: ['hiring', 'blacklist'] });
      void client.invalidateQueries({ queryKey: hiringKeys.imports() });
    },
  });
}

// ── Площадка ──────────────────────────────────────────────────────────────────────────────

export function useAccountsQuery(): UseQueryResult<ListResponse<AccountOut>, ApiError> {
  return useQuery<ListResponse<AccountOut>, ApiError>({
    queryKey: hiringKeys.accounts(),
    queryFn: ({ signal }) => listAccounts(signal),
    placeholderData: keepPreviousData,
  });
}

export function useExchangeLogQuery(
  accountId: string,
): UseQueryResult<ListResponse<ExchangeOut>, ApiError> {
  return useQuery<ListResponse<ExchangeOut>, ApiError>({
    queryKey: hiringKeys.exchange(accountId),
    queryFn: ({ signal }) => fetchExchangeLog(accountId, 100, signal),
  });
}

export function useVacanciesQuery(params: {
  account_id?: string;
  state?: string;
}): UseQueryResult<ListResponse<VacancyOut>, ApiError> {
  return useQuery<ListResponse<VacancyOut>, ApiError>({
    queryKey: hiringKeys.vacancies(params),
    queryFn: ({ signal }) => listVacancies(params, signal),
    placeholderData: keepPreviousData,
  });
}

export function useCreateAccountMutation(
  idempotencyKey: string,
): UseMutationResult<AccountOut, ApiError, { title: string; note?: string | null }> {
  const client = useQueryClient();
  return useMutation<AccountOut, ApiError, { title: string; note?: string | null }>({
    mutationFn: (body) => createAccount(body, idempotencyKey),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.accounts() });
      // Режим помощника заводится вместе с записью: список настроек тоже устарел.
      void client.invalidateQueries({ queryKey: hiringKeys.assistantSettings() });
    },
  });
}

export function useSetCredentialsMutation(
  accountId: string,
): UseMutationResult<
  AccountOut,
  ApiError,
  { kind: KeyKind; client_id: string; client_secret: string }
> {
  const client = useQueryClient();
  return useMutation<
    AccountOut,
    ApiError,
    { kind: KeyKind; client_id: string; client_secret: string }
  >({
    mutationFn: ({ kind, client_id, client_secret }) =>
      setCredentials(accountId, kind, { client_id, client_secret }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.accounts() });
    },
  });
}

export function useRefreshTokenMutation(
  accountId: string,
): UseMutationResult<TokenOut, ApiError, KeyKind> {
  const client = useQueryClient();
  return useMutation<TokenOut, ApiError, KeyKind>({
    mutationFn: (kind) => refreshToken(accountId, kind),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.accounts() });
      void client.invalidateQueries({ queryKey: hiringKeys.exchange(accountId) });
    },
  });
}

export function usePollApplicationsMutation(
  accountId: string,
): UseMutationResult<PollResultOut, ApiError, void> {
  const client = useQueryClient();
  return useMutation<PollResultOut, ApiError, void>({
    mutationFn: () => pollApplications(accountId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.all });
    },
  });
}

export function useCreateVacancyMutation(
  idempotencyKey: string,
): UseMutationResult<VacancyOut, ApiError, VacancyIn> {
  const client = useQueryClient();
  return useMutation<VacancyOut, ApiError, VacancyIn>({
    mutationFn: (body) => createVacancy(body, idempotencyKey),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['hiring', 'vacancies'] });
    },
  });
}

export function usePublishVacancyMutation(): UseMutationResult<VacancyOut, ApiError, string> {
  const client = useQueryClient();
  return useMutation<VacancyOut, ApiError, string>({
    mutationFn: (vacancyId) => publishVacancy(vacancyId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['hiring', 'vacancies'] });
      void client.invalidateQueries({ queryKey: ['hiring', 'exchange'] });
    },
  });
}

export function useRefreshVacancyStatusMutation(): UseMutationResult<VacancyOut, ApiError, string> {
  const client = useQueryClient();
  return useMutation<VacancyOut, ApiError, string>({
    mutationFn: (vacancyId) => refreshVacancyStatus(vacancyId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['hiring', 'vacancies'] });
    },
  });
}

export function useBulkEditMutation(): UseMutationResult<BulkEditOut, ApiError, BulkEditIn> {
  const client = useQueryClient();
  return useMutation<BulkEditOut, ApiError, BulkEditIn>({
    mutationFn: (body) => bulkEditVacancies(body),
    onSuccess: (итог) => {
      // Список перечитывается только после НАСТОЯЩЕЙ правки: предпросмотр ничего не менял, и
      // перезапрос после него — лишний обход сети на каждое нажатие «посмотреть».
      if (итог.применено > 0) {
        void client.invalidateQueries({ queryKey: ['hiring', 'vacancies'] });
      }
    },
  });
}

export function useBuildBulkUploadMutation(): UseMutationResult<BulkUploadOut, ApiError, string> {
  const client = useQueryClient();
  return useMutation<BulkUploadOut, ApiError, string>({
    mutationFn: (accountId) => buildBulkUpload(accountId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['hiring', 'vacancies'] });
    },
  });
}

// ── Переписка и помощник ──────────────────────────────────────────────────────────────────

export function useDialogsQuery(params: {
  status?: string;
  account_id?: string;
}): UseQueryResult<ListResponse<DialogRow>, ApiError> {
  return useQuery<ListResponse<DialogRow>, ApiError>({
    queryKey: hiringKeys.dialogs(params),
    queryFn: ({ signal }) => listDialogs({ ...params, limit: 200 }, signal),
    placeholderData: keepPreviousData,
  });
}

export function useDialogQuery(id: string): UseQueryResult<DialogLog, ApiError> {
  return useQuery<DialogLog, ApiError>({
    queryKey: hiringKeys.dialog(id),
    queryFn: ({ signal }) => fetchDialog(id, signal),
  });
}

/**
 * Действие над перепиской: подтвердить предложенный ответ, отклонить, написать самому,
 * вернуть помощника. Все четыре меняют одно и то же — ленту диалога и его строку в списке.
 */
function useDialogAction<V>(
  dialogId: string,
  mutationFn: (variables: V) => Promise<ActionResult>,
): UseMutationResult<ActionResult, ApiError, V> {
  const client = useQueryClient();
  return useMutation<ActionResult, ApiError, V>({
    mutationFn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.dialog(dialogId) });
      void client.invalidateQueries({ queryKey: ['hiring', 'dialogs'] });
    },
  });
}

export function useApproveMessageMutation(dialogId: string) {
  return useDialogAction<string>(dialogId, (messageId) => approveMessage(messageId));
}

export function useDiscardMessageMutation(dialogId: string) {
  return useDialogAction<string>(dialogId, (messageId) => discardMessage(messageId));
}

export function useSendMessageMutation(dialogId: string) {
  return useDialogAction<string>(dialogId, (text) => sendOperatorMessage(dialogId, text));
}

export function useResumeAssistantMutation(dialogId: string) {
  return useDialogAction<void>(dialogId, () => resumeAssistant(dialogId));
}

export function useAssistantSettingsQuery(): UseQueryResult<
  ListResponse<AssistantSettings>,
  ApiError
> {
  return useQuery<ListResponse<AssistantSettings>, ApiError>({
    queryKey: hiringKeys.assistantSettings(),
    queryFn: ({ signal }) => listAssistantSettings(signal),
    placeholderData: keepPreviousData,
  });
}

export function useSaveAssistantSettingsMutation(
  accountId: string,
): UseMutationResult<AssistantSettings, ApiError, AssistantSettingsIn> {
  const client = useQueryClient();
  return useMutation<AssistantSettings, ApiError, AssistantSettingsIn>({
    mutationFn: (body) => saveAssistantSettings(accountId, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: hiringKeys.assistantSettings() });
      // Режим меняет поведение помощника во всех открытых переписках этой записи.
      void client.invalidateQueries({ queryKey: ['hiring', 'dialogs'] });
    },
  });
}

export function useScriptQuery(
  accountId: string | undefined,
): UseQueryResult<ListResponse<ScriptStep>, ApiError> {
  return useQuery<ListResponse<ScriptStep>, ApiError>({
    queryKey: hiringKeys.script(accountId),
    queryFn: ({ signal }) => listScript(accountId, signal),
    // Сценарий разговора меняется раз в месяц: держим его пять минут, иначе каждый список
    // формулировок стучится за одним и тем же набором шагов.
    staleTime: 5 * 60 * 1000,
  });
}

export function useLibraryQuery(params: {
  step_code?: string;
  status?: string;
  account_id?: string;
}): UseQueryResult<ListResponse<LibraryEntry>, ApiError> {
  return useQuery<ListResponse<LibraryEntry>, ApiError>({
    queryKey: hiringKeys.library(params),
    queryFn: ({ signal }) => listLibrary({ ...params, limit: 500 }, signal),
    placeholderData: keepPreviousData,
  });
}

export function useCreatePhraseMutation(): UseMutationResult<
  LibraryEntry,
  ApiError,
  LibraryDraftIn
> {
  const client = useQueryClient();
  return useMutation<LibraryEntry, ApiError, LibraryDraftIn>({
    mutationFn: (body) => createLibraryDraft(body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['hiring', 'library'] });
    },
  });
}

export function useEditPhraseMutation(): UseMutationResult<
  LibraryEntry,
  ApiError,
  { entryId: string; text: string }
> {
  const client = useQueryClient();
  return useMutation<LibraryEntry, ApiError, { entryId: string; text: string }>({
    mutationFn: ({ entryId, text }) => editLibraryEntry(entryId, text),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['hiring', 'library'] });
    },
  });
}

export function useDeletePhraseMutation(): UseMutationResult<void, ApiError, string> {
  const client = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (entryId) => deleteLibraryEntry(entryId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['hiring', 'library'] });
    },
  });
}

export function useApprovePhraseMutation(): UseMutationResult<LibraryEntry, ApiError, string> {
  const client = useQueryClient();
  return useMutation<LibraryEntry, ApiError, string>({
    mutationFn: (entryId) => approveLibraryEntry(entryId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['hiring', 'library'] });
    },
  });
}

export function useRetirePhraseMutation(): UseMutationResult<LibraryEntry, ApiError, string> {
  const client = useQueryClient();
  return useMutation<LibraryEntry, ApiError, string>({
    mutationFn: (entryId) => retireLibraryEntry(entryId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['hiring', 'library'] });
    },
  });
}
